import { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useClinicStaff, type ClinicStaffMember } from '@/hooks/useClinicStaff';

interface NotificationRecipientsSectionProps {
  policyOwner: string;
  selectedRecipients: string[];
  onRecipientsChange: (recipients: string[]) => void;
  status: 'draft' | 'pending_approval';
}

export function NotificationRecipientsSection({
  policyOwner,
  selectedRecipients,
  onRecipientsChange,
  status,
}: NotificationRecipientsSectionProps) {
  const { data: staff = [] } = useClinicStaff();
  const [addUserId, setAddUserId] = useState('');

  // Compute default recipients based on roles
  const defaultRecipientIds = useMemo(() => {
    const ids = new Set<string>();
    staff.forEach((member) => {
      // Policy owner always gets notifications
      if (member.id === policyOwner) ids.add(member.id);
      // Admins and owners get approval notifications
      if (member.role === 'admin' || member.role === 'owner') ids.add(member.id);
    });
    return ids;
  }, [staff, policyOwner]);

  // Initialize recipients with defaults when staff loads
  useEffect(() => {
    if (staff.length > 0 && selectedRecipients.length === 0) {
      onRecipientsChange(Array.from(defaultRecipientIds));
    }
  }, [staff, defaultRecipientIds]);

  const selectedSet = useMemo(() => new Set(selectedRecipients), [selectedRecipients]);

  const toggleRecipient = (id: string, checked: boolean) => {
    if (checked) {
      onRecipientsChange([...selectedRecipients, id]);
    } else {
      onRecipientsChange(selectedRecipients.filter((r) => r !== id));
    }
  };

  const handleAddUser = () => {
    if (addUserId && !selectedSet.has(addUserId)) {
      onRecipientsChange([...selectedRecipients, addUserId]);
      setAddUserId('');
    }
  };

  const removeRecipient = (id: string) => {
    onRecipientsChange(selectedRecipients.filter((r) => r !== id));
  };

  const staffById = useMemo(() => {
    const map = new Map<string, ClinicStaffMember>();
    staff.forEach((s) => map.set(s.id, s));
    return map;
  }, [staff]);

  // Staff not yet selected, for the add dropdown
  const availableToAdd = staff.filter((s) => !selectedSet.has(s.id));

  const getRoleBadge = (member: ClinicStaffMember) => {
    if (member.id === policyOwner) return 'Owner';
    if (member.role === 'admin') return 'Admin';
    if (member.role === 'owner') return 'Owner';
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notifications</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          These people will be notified when this policy moves to Pending Approval. No notifications are sent while the policy is in Draft.
        </p>

        {/* Selected recipients list */}
        <div className="space-y-2">
          <Label>Recipients</Label>
          <div className="space-y-1">
            {selectedRecipients.map((id) => {
              const member = staffById.get(id);
              if (!member) return null;
              const roleBadge = getRoleBadge(member);
              return (
                <div
                  key={id}
                  className="flex items-center justify-between py-1.5 px-2 rounded-md border bg-muted/20"
                >
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked
                      onCheckedChange={(checked) => toggleRecipient(id, checked as boolean)}
                    />
                    <span className="text-sm">{member.name}</span>
                    {roleBadge && (
                      <Badge variant="secondary" className="text-xs">
                        {roleBadge}
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                    onClick={() => removeRecipient(id)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              );
            })}
            {selectedRecipients.length === 0 && (
              <p className="text-sm text-muted-foreground italic">No recipients selected</p>
            )}
          </div>
        </div>

        {/* Add recipient */}
        {availableToAdd.length > 0 && (
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <Label className="text-xs">Add recipient</Label>
              <Select value={addUserId} onValueChange={setAddUserId}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select staff member..." />
                </SelectTrigger>
                <SelectContent>
                  {availableToAdd.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} ({s.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddUser}
              disabled={!addUserId}
              className="h-9"
            >
              Add
            </Button>
          </div>
        )}

        {status === 'draft' && (
          <div className="rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
            ℹ️ This policy is currently a draft. No notifications will be sent until it moves to Pending Approval.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
